import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Upload, Save, Printer, FileDown} from 'lucide-react'
import toast from "react-hot-toast"
import { useGuias } from "../context/GuiasContext"

interface NovaGuiaProps {
  editingGuiaId?: string
  onSaveComplete?: () => void
}

export default function NovaGuia({ editingGuiaId, onSaveComplete }: NovaGuiaProps) {
  const { addGuia, updateGuia, getGuiaById, logoUrl, dadosPrefeitura, cemiterios } = useGuias()
  const [formData, setFormData] = useState({
    numero: "",
    nomeFalecido: "",
    nomeMae: "",
    nomePai: "",
    naturalidade: "",
    cor: "",
    dataNascimento: "",
    idade: "",
    sexo: "",
    causaMorte: "",
    localObito: "",
    dataObito: "",
    horaObito: "",
    obitoNumero: "",
    fls: "",
    livroNumero: "",
    cartorio: "",
    dataSepultamento: "",
    cemiterio: "",
    valorPago: "",
    talaoNumero: "",
    nomeSolicitante: "",
    cpfSolicitante: "",
    certidaoFile: null as File | null,
    certidaoBase64: "" as string,
  })

  const formatValor = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (!numbers) return ""
    const amount = parseFloat(numbers) / 100
    return amount.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const calcularIdade = (dataNascimento: string, dataObito: string) => {
    if (!dataNascimento) return ""
    
    const nascimento = new Date(dataNascimento + "T12:00:00")
    // Usa data de óbito se disponível, senão usa a data atual
    const dataReferencia = dataObito ? new Date(dataObito + "T12:00:00") : new Date()
    
    let idade = dataReferencia.getFullYear() - nascimento.getFullYear()
    const mesNascimento = nascimento.getMonth()
    const mesReferencia = dataReferencia.getMonth()
    
    if (mesReferencia < mesNascimento || (mesReferencia === mesNascimento && dataReferencia.getDate() < nascimento.getDate())) {
      idade--
    }
    
    return idade >= 0 ? idade.toString() : ""
  }

  // useEffect para recalcular idade quando as datas mudarem
  useEffect(() => {
    if (formData.dataNascimento) {
      const idadeCalculada = calcularIdade(formData.dataNascimento, formData.dataObito)
      if (idadeCalculada !== formData.idade) {
        setFormData(prev => ({ ...prev, idade: idadeCalculada }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dataNascimento, formData.dataObito])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    if (name === "valorPago") {
      setFormData(prev => ({ ...prev, [name]: formatValor(value) }))
    } else if (name === "dataNascimento") {
      const idadeCalculada = calcularIdade(value, formData.dataObito)
      setFormData(prev => ({ ...prev, [name]: value, idade: idadeCalculada }))
    } else if (name === "dataObito") {
      const idadeCalculada = calcularIdade(formData.dataNascimento, value)
      setFormData(prev => ({ ...prev, [name]: value, idade: idadeCalculada }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      
      // Converter para base64
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result as string
        setFormData(prev => ({ 
          ...prev, 
          certidaoFile: file,
          certidaoBase64: base64String 
        }))
        toast.success("Certidão de óbito anexada com sucesso!")
      }
      reader.onerror = () => {
        toast.error("Erro ao carregar o arquivo")
      }
      reader.readAsDataURL(file)
    }
  }

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
    }
    return value
  }

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value)
    setFormData(prev => ({ ...prev, cpfSolicitante: formatted }))
  }

  // Carregar dados da guia se estiver editando
  useEffect(() => {
    if (editingGuiaId) {
      const guia = getGuiaById(editingGuiaId)
      if (guia) {
        // Extrair apenas a parte da data (YYYY-MM-DD) do formato ISO
        const extrairData = (dataISO: string) => {
          if (!dataISO) return ""
          return dataISO.split("T")[0]
        }
        
        setFormData({
          numero: guia.numero,
          nomeFalecido: guia.nomeFalecido,
          nomeMae: guia.nomeMae,
          nomePai: guia.nomePai,
          naturalidade: guia.naturalidade,
          cor: guia.cor,
          dataNascimento: extrairData(guia.dataNascimento || ""),
          idade: guia.idade,
          sexo: guia.sexo || "",
          causaMorte: guia.causaMorte,
          localObito: guia.localObito,
          dataObito: extrairData(guia.dataObito),
          horaObito: guia.horaObito,
          obitoNumero: guia.obitoNumero,
          fls: guia.fls,
          livroNumero: guia.livroNumero,
          cartorio: guia.cartorio,
          dataSepultamento: extrairData(guia.dataSepultamento),
          cemiterio: guia.cemiterio,
          valorPago: guia.valorPago,
          talaoNumero: guia.talaoNumero,
          nomeSolicitante: guia.nomeSolicitante,
          cpfSolicitante: guia.cpfSolicitante,
          certidaoFile: guia.certidaoFile ? new File([], guia.certidaoFile) : null,
          certidaoBase64: guia.certidaoBase64 || "",
        })
      }
    }
  }, [editingGuiaId, getGuiaById])

  const handleSave = async () => {
    // Validação básica
    if (!formData.numero || !formData.nomeFalecido || !formData.nomeMae || !formData.dataObito) {
      toast.error("Preencha os campos obrigatórios: Número da Guia, Nome do Falecido, Nome da Mãe e Data do Óbito")
      return
    }

    // Garantir que as datas estejam no formato YYYY-MM-DD (sem T...) para o contexto corrigir
    const dataObitoLimpa = formData.dataObito.split("T")[0]
    const dataSepultamentoLimpa = formData.dataSepultamento ? formData.dataSepultamento.split("T")[0] : ""
    const dataNascimentoLimpa = formData.dataNascimento ? formData.dataNascimento.split("T")[0] : ""

    const dadosParaSalvar = {
      ...formData,
      dataObito: dataObitoLimpa,
      dataSepultamento: dataSepultamentoLimpa,
      dataNascimento: dataNascimentoLimpa,
      certidaoFile: formData.certidaoFile?.name || "",
      certidaoBase64: formData.certidaoBase64
    }

    if (editingGuiaId) {
      await updateGuia(editingGuiaId, dadosParaSalvar)
    } else {
      await addGuia(dadosParaSalvar)
      // Limpar formulário
      setFormData({
        numero: "",
        nomeFalecido: "",
        nomeMae: "",
        nomePai: "",
        naturalidade: "",
        cor: "",
        dataNascimento: "",
        idade: "",
        sexo: "",
        causaMorte: "",
        localObito: "",
        dataObito: "",
        horaObito: "",
        obitoNumero: "",
        fls: "",
        livroNumero: "",
        cartorio: "",
        dataSepultamento: "",
        cemiterio: "",
        valorPago: "",
        talaoNumero: "",
        nomeSolicitante: "",
        cpfSolicitante: "",
        certidaoFile: null,
        certidaoBase64: "",
      })
    }
    
    if (onSaveComplete) {
      onSaveComplete()
    }
  }

  const handlePrint = () => {
    if (!formData.nomeFalecido) {
      toast.error("Preencha pelo menos o nome do falecido antes de imprimir")
      return
    }
    window.print()
    toast.success("Documento enviado para impressão")
  }

  const handleExportPDF = () => {
    if (!formData.nomeFalecido) {
      toast.error("Preencha pelo menos o nome do falecido antes de exportar")
      return
    }
    window.print()
    toast.success("PDF gerado com sucesso!")
  }

  return (
    <div className="space-y-6">
      {/* Header com Logo e Informações Institucionais */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8 border border-gray-200 print-guia"
      >
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 mb-4 pb-4 border-b border-gray-200">
          <img
            src={logoUrl}
            alt="Logo do Município"
            className="h-16 sm:h-20 w-auto object-contain"
          />
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-1">
              {dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal"}
            </h2>
            <p className="text-xs text-gray-600 leading-tight">{dadosPrefeitura?.secretaria}</p>
            <p className="text-xs text-gray-600 leading-tight">{dadosPrefeitura?.departamento}</p>
            <p className="text-xs text-gray-600 mt-1 leading-tight">
              {dadosPrefeitura?.endereco} | CEP: {dadosPrefeitura?.cep}
            </p>
            <p className="text-xs text-gray-600 leading-tight">
              CNPJ: {dadosPrefeitura?.cnpj} | Tel: {dadosPrefeitura?.telefone}
            </p>
          </div>
        </div>

        <h3 className="text-base sm:text-lg font-bold text-center text-gray-800 mb-4">
          GUIA DE SEPULTAMENTO
        </h3>

        {/* Número da Guia */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Número da Guia *
          </label>
          <input
            type="text"
            name="numero"
            value={formData.numero}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="Ex: 001/2024"
            required
          />
        </div>

        {/* Informações do Falecido */}
        <div className="mb-5">
          <h4 className="text-base font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-300">
            Informações do Falecido
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo do Falecido *
              </label>
              <input
                type="text"
                name="nomeFalecido"
                value={formData.nomeFalecido}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Nome completo"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome da Mãe *
              </label>
              <input
                type="text"
                name="nomeMae"
                value={formData.nomeMae}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Nome da mãe"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Pai *
              </label>
              <input
                type="text"
                name="nomePai"
                value={formData.nomePai}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Nome do pai"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Naturalidade *
              </label>
              <input
                type="text"
                name="naturalidade"
                value={formData.naturalidade}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Cidade/Estado"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cor *
              </label>
              <input
                type="text"
                name="cor"
                value={formData.cor}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Cor/Raça"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data de Nascimento *
              </label>
              <input
                type="date"
                name="dataNascimento"
                value={formData.dataNascimento}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Idade *
              </label>
              <input
                type="text"
                name="idade"
                value={formData.idade}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent bg-gray-50"
                placeholder="Calculado automaticamente"
                readOnly
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sexo *
              </label>
              <select
                name="sexo"
                value={formData.sexo}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              >
                <option value="">Selecione</option>
                <option value="Masculino">Masculino</option>
                <option value="Feminino">Feminino</option>
              </select>
            </div>
          </div>
        </div>

        {/* Informações do Óbito */}
        <div className="mb-5">
          <h4 className="text-base font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-300">
            Informações do Óbito
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Causa da Morte *
              </label>
              <input
                type="text"
                name="causaMorte"
                value={formData.causaMorte}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Causa da morte"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Local do Óbito *
              </label>
              <input
                type="text"
                name="localObito"
                value={formData.localObito}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Local do óbito"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data do Óbito *
              </label>
              <input
                type="date"
                name="dataObito"
                value={formData.dataObito}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora do Óbito *
              </label>
              <input
                type="time"
                name="horaObito"
                value={formData.horaObito}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
          </div>
        </div>

        {/* Informações do Sepultamento */}
        <div className="mb-5">
          <h4 className="text-base font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-300">
            Informações do Sepultamento
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Óbito Nº *
              </label>
              <input
                type="text"
                name="obitoNumero"
                value={formData.obitoNumero}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Número do óbito"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fls. *
              </label>
              <input
                type="text"
                name="fls"
                value={formData.fls}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Folhas"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Livro Nº *
              </label>
              <input
                type="text"
                name="livroNumero"
                value={formData.livroNumero}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Número do livro"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cartório *
              </label>
              <input
                type="text"
                name="cartorio"
                value={formData.cartorio}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Nome do cartório"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data do Sepultamento *
              </label>
              <input
                type="date"
                name="dataSepultamento"
                value={formData.dataSepultamento}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cemitério *
              </label>
              <select
                name="cemiterio"
                value={formData.cemiterio}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                required
              >
                <option value="">Selecione um cemitério</option>
                {cemiterios.map((cem) => (
                  <option key={cem._id} value={cem.nome}>
                    {cem.codigo} - {cem.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor Pago (R$) *
              </label>
              <input
                type="text"
                name="valorPago"
                value={formData.valorPago}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="0,00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Conforme Talão Nº *
              </label>
              <input
                type="text"
                name="talaoNumero"
                value={formData.talaoNumero}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Número do talão"
                required
              />
            </div>
          </div>
        </div>

        {/* Informações do Solicitante */}
        <div className="mb-5">
          <h4 className="text-base font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-300">
            Informações do Solicitante
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Solicitante *
              </label>
              <input
                type="text"
                name="nomeSolicitante"
                value={formData.nomeSolicitante}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="Nome completo do solicitante"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CPF do Solicitante *
              </label>
              <input
                type="text"
                name="cpfSolicitante"
                value={formData.cpfSolicitante}
                onChange={handleCPFChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                placeholder="000.000.000-00"
                maxLength={14}
                required
              />
            </div>
          </div>
        </div>

        {/* Upload de Certidão */}
        <div className="mb-5">
          <h4 className="text-base font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-300">
            Certidão de Óbito
          </h4>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#004161] transition-colors">
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <label className="cursor-pointer">
              <span className="text-[#004161] font-medium hover:text-[#005580]">
                Clique para fazer upload
              </span>
              <span className="text-gray-500"> ou arraste o arquivo aqui</span>
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </label>
            <p className="text-xs text-gray-500 mt-2">
              Formatos aceitos: PDF, JPG, PNG (máx. 10MB)
            </p>
            {formData.certidaoFile && (
              <div className="mt-4 text-sm text-green-600 font-medium">
                ✓ Arquivo anexado: {formData.certidaoFile.name}
              </div>
            )}
          </div>
        </div>

        {/* Assinaturas */}
        <div className="mb-5">
          <h4 className="text-base font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-300">
            Assinaturas
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border-t-2 border-gray-400 pt-2 text-center">
              <p className="text-sm font-medium text-gray-700">Antônio Fernandes Neto</p>
              <p className="text-xs text-gray-500 mt-1">Coordador SEMPLA</p>
               <p className="text-xs text-gray-500 mt-1">MATRÍCULA - 130399-7</p>
            </div>
            <div className="border-t-2 border-gray-400 pt-2 text-center">
              <p className="text-sm font-medium text-gray-700">Assinatura do Solicitante</p>
              <p className="text-xs text-gray-500 mt-1">{formData.nomeSolicitante || "Nome do Solicitante"}</p>
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-end pt-6 border-t border-gray-200 no-print">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#004161] text-white rounded-lg hover:bg-[#005580] transition-colors shadow-lg text-sm sm:text-base w-full sm:w-auto"
          >
            <Save className="w-4 h-4 sm:w-5 sm:h-5" />
            Salvar Guia
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExportPDF}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#004161] text-white rounded-lg hover:bg-[#005580] transition-colors shadow-lg text-sm sm:text-base w-full sm:w-auto"
          >
            <FileDown className="w-4 h-4 sm:w-5 sm:h-5" />
            Salvar em PDF
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handlePrint}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#004161] text-white rounded-lg hover:bg-[#005580] transition-colors shadow-lg text-sm sm:text-base w-full sm:w-auto"
          >
            <Printer className="w-4 h-4 sm:w-5 sm:h-5" />
            Imprimir
          </motion.button>
        </div>
      </motion.div>
    </div>
  )
}
